import { useState, useEffect, useContext, useRef } from 'react';

import {
  bodyScrollCntl,
  formatDate, formatTime, formatTimeAddMin,
  requestApi,
} from '../../../../../../../util/utility';
import { LiveReserveContext } from '../../../LiveLessonReseveContext';
import Loading from '../../../../../../common/loading/Loading';

import {
  LiveReserveSituationDisplaySituations,
  LiveReserveSituationValue,
  LiveReserveSituationItem,
} from '../../../../../../../models/lesson/live/reserve/situation/LiveReserveSituation';


/**
 * 予約 / 予約リクエストの確認モーダル
 *
 * ★SPEAKOUT-1853 送信先が2つある。
 *   - 予約     … 予約フォームをそのまま submit（/lesson/live）＝その場で確定・チケット消費
 *   - リクエスト … API へ Ajax（/backend/api/functions/user/lesson/live/request）
 *                 ＝事務局へメール・確定しない。**このモーダルから出ない**
 *
 * 🔴 リクエストは以前 /inquiry へフォームPOSTしてページ遷移していた。検証で弾かれると
 *   【リクエスト用の入力欄が無いお問い合わせ画面】へ飛ばされ、入力を失った状態で
 *   行き止まりになっていた（実機確認 C-12）。Ajax にして、確認 → 送信 → 完了 を
 *   すべてこのモーダルの中で完結させる。
 */
function LiveReserveSubmitConfirm(props: any) {

  const isDispConfirm = props.isDispConfirm;
  const setIsDispConfirm = props.setIsDispConfirm;

  const {
    lessonTermMinute,
    user,
    instructors, noSlotInstructors, instId,
    lsnMin, isTrial,
    calendar, selectedScheduleId,
    displaySituations, selectedSituation,
    requestContent,
    isRequestMode, requestPrefs,
    formRef,
  } = useContext(LiveReserveContext);

  const [dispInstName, setDispInstName] = useState<string>('');
  const [dispTime, setDispTime] = useState<string>('');
  const [dispDate, setDispDate] = useState<string>('');
  const [dispDateTime, setDispDateTime] = useState<string>('');
  const [dispSitucation, setDispSituation] = useState<string>('');

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // リクエストの送信結果。完了するとモーダルの中身が完了表示に変わる
  const [isRequestDone, setIsRequestDone] = useState<boolean>(false);
  const [requestErrors, setRequestErrors] = useState<string[]>([]);
  // 🔴 二度押しの判定は ref。setState は非同期で、連打の2回目が再レンダリング前に入ると
  //   古い値を読んで素通りする（記述式のころ、同じ生徒が1分以内に同じ講師へ3連投した実データがある）
  const isSubmittingRef = useRef<boolean>(false);


  // モーダル表示時に背景スクロールを止める
  useEffect(() => {
    if (isDispConfirm) {
      bodyScrollCntl(false);
    } else {
      bodyScrollCntl(true);
    }

    // アンマウント時にも戻す（安全のため）
    return () => {
      bodyScrollCntl(true);
    };
  }, [isDispConfirm]);


  const closeModal = () => {
    if (isSubmitting) return;
    // ★リクエスト送信済みで閉じるときは読み直す。入力が残ったままだと、そのまま
    //   もう一度「予約リクエストする」が押せてしまい、同じ内容が二重に事務局へ届く
    if (isRequestDone) {
      window.location.reload();   // ★href を書き換えない（?inst_id= が落ちて講師の選択が消える）
      return;
    }
    setIsDispConfirm(false);
  }


  // 講師名
  //   ★SPEAKOUT-1853 空き枠が1つも無い講師は予約用の一覧($instructors)から外れているので、
  //     リクエストで指名できるよう noSlotInstructors も併せて探す
  useEffect(() => {
    let newInstName = '講師を指定しない';
    if(instId) {
      const allInstructors = (instructors || []).concat(noSlotInstructors || []);
      const selectInst = allInstructors.find((inst: any) => {
        return inst.inst_id == instId
      })
      if(selectInst?.name) {
        newInstName = selectInst.name;
      }
    }
    setDispInstName(newInstName);
  }, [instructors, noSlotInstructors, instId])

  // レッスン時間
  //   ★SPEAKOUT-1853 リクエストのときは「いつ消費するか」まで書く。
  //     リクエストは inquiry の登録とメールだけで、チケットも無料回数も消費しない
  //     （実機確認 C-12b）。同じモーダルに「この時点ではご予約は確定しません」と
  //     出しているのに枚数だけ素で並ぶと、今引かれたと読めてしまう。
  useEffect(() => {
    let cost = '';
    switch(lsnMin) {
      case lessonTermMinute:
        cost = isTrial ? '無料1回分' : '3チケット';
        break;
      case lessonTermMinute*2:
        cost = isTrial ? '無料2回分' : '6チケット';
        break;
      case lessonTermMinute*3:
        cost = isTrial ? '無料3回分' : '9チケット';
        break;
      default:
        setDispTime('');
        return;
    }
    setDispTime(lsnMin + '分（' + (isRequestMode ? 'ご予約が決まった時点で' : '') + cost + '）');
  }, [lsnMin, isTrial, isRequestMode])

  // 予約日時
  useEffect(() => {
    for(const date in calendar) {
      const times = calendar[date];
      for(const time in times) {
        if(times[time] == selectedScheduleId) {
          setDispDate(date);
          // 時間を"HH:MM"に整形
          setDispDateTime(`${formatTime(time)} 〜 ${formatTimeAddMin(time, lsnMin)}`);
        }
      }
    }
  }, [calendar, selectedScheduleId])

  // 実践内容
  useEffect(() => {
    if (!displaySituations) return;
    for (const situation of displaySituations as LiveReserveSituationDisplaySituations) {
      const valueMap: LiveReserveSituationValue = situation.value;
      for (const key in valueMap) {
        const category: LiveReserveSituationItem = valueMap[key];
        if (category.id === selectedSituation) {
          setDispSituation(category.situation);
        }
      }
    }
  }, [displaySituations, selectedSituation]);


  /**
   * 予約リクエストの送信 (SPEAKOUT-1853)
   *
   * 送信先は Ajax の API（app/backend/api/functions/user/lesson/live/request.php）。
   * ★このモーダルは予約フォーム <form action="/lesson/live"> の【中】に描画されているので、
   *   ページ遷移する送信（<form> の submit）はできない。入れ子の <form> は submit イベントが
   *   React に届かず、検証をすり抜けて遷移してしまう。加えて遷移させると、サーバーの検証で
   *   弾かれたときに入力を失って行き止まりになる（実機確認 C-12）。
   *   ＝**この関数は画面遷移しない**。結果はこのモーダルの中に出す。
   * ★講師名・実践したい内容の【名称】は送らない。サーバーが inst_profile と
   *   $l_situations['user'] から引き直す（POST値で本文を改ざんされないため）。
   */
  const submitRequest = async (): Promise<boolean> => {
    // ★名前・メールは送らない。サーバーがセッションから取る（なりすまし防止）。
    //   カテゴリも同様にサーバー側の liveInstRequestCategoryId() が決める。
    const params: { [key: string]: any } = {
      inc:            '/backend/api/functions/user/lesson/live/request',
      inst_id:        instId ? String(instId) : '',
      lesson_minutes: String(lsnMin),
      situation:      selectedSituation ? String(selectedSituation) : '',
      memo:           requestContent,
    };
    requestPrefs.forEach((row: any, index: number) => {
      const n = index + 1;
      params['pref_date' + n]          = row.date;
      params['pref_time' + n]          = row.start;
      params['pref_time' + n + '_end'] = row.end;
    });

    try {
      const resp = await requestApi(params);
      if (resp && resp.ok) {
        setRequestErrors([]);
        setIsRequestDone(true);
        return true;
      }
      // サーバーの検証で弾かれた場合。画面側の検証と二重になっているので通常は起きないが、
      // 起きたときに黙って消えないよう、理由をこの場に出す
      const messages = resp && resp.errors ? Object.keys(resp.errors).map((k) => resp.errors[k]) : [];
      setRequestErrors(messages.length ? messages : ['送信できませんでした。時間をおいて再度お試しください。']);
      return false;
    } catch (e) {
      setRequestErrors(['送信できませんでした。通信状況をご確認のうえ、再度お試しください。']);
      return false;
    }
  };


  // 送信
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    if (isRequestMode) {
      // 予約リクエスト（事務局へメール・予約は確定しない）。
      // ★ページ遷移しないので、成功しても【モーダルを閉じない】。完了表示に切り替える。
      try {
        await submitRequest();
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
      return;
    }

    // 予約（その場で確定）。submit でページが遷移するので、後片付けは従来どおり時間差で行う
    try {
      if (!formRef.current) return;
      formRef.current.submit();
      setTimeout(() => {
        // モーダルを閉じる
        setIsDispConfirm(false);
      }, 300);
    } finally {
      setTimeout(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }, 500);
    }
  };



  return (
    <>
    {isDispConfirm &&
      <>
      {/* backdrop */}
      <div className="modal-backdrop fade in"></div>

      {/* modal */}
      <div
        id="practice_confirm_modal"
        className="modal fade in"
        style={{ display: 'block', overflow: 'auto' }}
        tabIndex={-1}
      >
        <div className="modal-dialog">
          <div className="modal-content">

            <div className="modal-header">
              {!isSubmitting &&
                <button
                  type="button"
                  className="close"
                  onClick={closeModal}
                  aria-hidden="true"
                >
                  <big>×</big>
                </button>
              }
              <h4 className="modal-title">
                <small>
                  {isRequestDone ? 'リクエストを受け付けました' : '以下の内容で送信してよろしいですか？'}
                </small>
              </h4>
            </div>

            <div className="modal-body text-center">

              {/* 送信できたとき。ページ遷移しないので、ここで結果を伝えきる */}
              {isRequestDone &&
                <div className="text-left">
                  <div className="alert alert-success" style={{padding: '10px'}}>
                    <small>
                      ご希望の日時を<b>事務局へお送りしました</b>。<br />
                      <b>この時点ではご予約は確定していません。</b>
                      事務局でご希望に沿った枠のご用意を調整のうえ、ご連絡いたします。
                    </small>
                  </div>
                  <p className="small">
                    ご登録のメールアドレスへ、受付内容の控えをお送りしました。
                  </p>
                  <div className="text-center">
                    <input
                      type="button"
                      onClick={closeModal}
                      className="btn btn-info btn_customize"
                      value="閉じる"
                    />
                  </div>
                </div>
              }

              {/* 送信できなかったとき。黙って消えると「送れたのか分からない」になる */}
              {!isRequestDone && requestErrors.length > 0 &&
                <div className="alert alert-danger text-left" style={{padding: '10px'}}>
                  {requestErrors.map((message, index) => (
                    <div key={index}><small>{message}</small></div>
                  ))}
                </div>
              }

              {/* ★リクエストは予約が確定しない。取り違えると生徒が「予約できたつもり」になる */}
              {!isRequestDone && isRequestMode &&
                <div className="alert alert-warning text-left" style={{padding: '10px'}}>
                  <small>
                    ご希望の日時を<b>事務局へリクエスト</b>します。
                    <b>この時点ではご予約は確定しません。</b><br />
                    事務局でご希望に沿った枠のご用意を調整のうえ、ご連絡いたします。
                  </small>
                </div>
              }

              {!isRequestDone &&
              <table className="table table-bordered table-condensed table-striped small">
                <tbody>
                  <tr>
                    <td className="text-right text-nowrap">講師</td>
                    <td className="text-left" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {dispInstName}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right text-nowrap">レッスン時間</td>
                    <td className="text-left">
                      {dispTime}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right text-nowrap">
                      {isRequestMode ? '希望日時' : '予約日時'}
                    </td>
                    <td className="text-left">
                      {isRequestMode ?
                        requestPrefs.map((row: any, index: number) => (
                          <div key={index}>
                            第{index + 1}希望&nbsp;{formatDate(row.date)}&nbsp;
                            <br className="visible-xs" />
                            {row.start} 〜 {row.end}
                          </div>
                        ))
                      :
                        <>
                          {dispDate}&nbsp;&nbsp;
                          <br className="visible-xs" />
                          {dispDateTime}
                        </>
                      }
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right text-nowrap">実践内容</td>
                    <td className="text-left">
                      {dispSitucation}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right text-nowrap">コメント</td>
                    <td className="text-left">
                      {requestContent}
                    </td>
                  </tr>
                </tbody>
              </table>
              }

              {!isRequestDone &&
              <div className="text-center">
                {isSubmitting ?
                  <Loading />
                :
                  <>
                    <input
                      style={{ marginBottom: '10px', marginRight: '5px', maxWidth: '100%', whiteSpace: 'normal', }}
                      type="button"
                      onClick={handleSubmit}
                      id="doReserveBtn"
                      className="btn btn-danger btn_customize"
                      value={isRequestMode ? 'この内容でリクエストする' : 'この内容で予約する'}
                      disabled={isSubmitting}
                    />
                    <input
                      style={{ marginBottom: '10px', marginLeft: '5px' }}
                      type="button"
                      onClick={closeModal}
                      className="btn btn-info btn_customize"
                      value="戻る"
                    />
                  </>
                }
              </div>
              }

            </div>
          </div>
        </div>
      </div>
    </>
    }
    </>
  );
}

export default LiveReserveSubmitConfirm;
